import classNames from 'classnames';
import {
  dismissForm,
  finishForm,
  formDidTransitionToNext,
  formDidTransitionToPrev,
  setFormAwaitingValidation,
  setFormError,
  setFormStageIndex,
  startForm,
  updateFormData,
} from 'js/actions';
import React from 'react';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import UserConfirmation from '../../route_utils/UserConfirmation';
import { SILENT_BLOCK_MESSAGE } from '../../route_utils/getUserConfirmation';
import FormErrorComponent from './FormErrorComponent';
import FormHeader from './FormHeader';

import './Form.scss';

const exitWorkflowMessage =
  'Are you sure you want to exit this editor?  All of your current changes will be discarded.';

class Form extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.constructor.getDerivedStateFromProps(props);

    this._handleWindowResize = this._handleWindowResize.bind(this);
    this._measureContainerDim = this._measureContainerDim.bind(this);

    this._handleBackButtonClicked = this._handleBackButtonClicked.bind(this);
    this._handleStageClicked = this._handleStageClicked.bind(this);

    this._handleNextButtonClicked = this._handleNextButtonClicked.bind(this);

    this._handleCancelButtonClicked = this._handleCancelButtonClicked.bind(this);
    this._onNavigationModalResponse = this._onNavigationModalResponse.bind(this);

    this._handleValidationSuccess = this._handleValidationSuccess.bind(this);
    this._handleValidationError = this._handleValidationError.bind(this);
    this._completeNextStageTransition = this._completeNextStageTransition.bind(this);
    this._setValidationStatus = this._setValidationStatus.bind(this);
  }

  static getDerivedStateFromProps(props, state = null) {
    const children = React.Children.toArray(props.children);
    const numStages = children.length;

    const stageComponents = children.map((item, index) => {
      return {
        nodeTitle: item.props.title,
        showBackButton: item.props.showBackButton,
        backButtonText: item.props.backButtonText,
        nextButtons: item.props.nextButtons ?? [
          {
            label:
              index < numStages - 1
                ? (item.props.nextButtonText ?? 'Continue')
                : (props.submitButtonText ?? 'Done'),
            validationMethod: item.props.validationMethod,
          },
        ],
        contentHeight: item.props.contentHeight,
        onEnter: item.props.onEnter,
        disabled: item.props.disabled === true,
        disabledTooltip: item.props.disabledTooltip ?? null,
      };
    });

    const newState = {
      children: children,
      stageComponents: stageComponents,
      numStages: numStages,
    };

    // Only set initial values if they haven't been set yet (state is null)
    if (!state) {
      newState.initialSourceConfig =
        props?.sourceBeingEdited?.source_config ?? props?.initialSourceConfig ?? {};
      newState.initialApiConfig = props?.apiConfig ?? props?.initialApiConfig ?? {};
      newState.displayUserConfirmationModal = false;
      newState.showSaveConfirmation = false;
      newState.pendingValidation = null;
    } else {
      // Preserve existing initial values to prevent false change detection
      newState.initialSourceConfig = state.initialSourceConfig;
      newState.initialApiConfig = state.initialApiConfig;

      // If we have apiConfig in props but our initialApiConfig is empty, update it
      if (props?.apiConfig && Object.keys(state.initialApiConfig).length === 0) {
        newState.initialApiConfig = props.apiConfig;
      }
    }

    return newState;
  }

  _handleCancelButtonClicked() {
    this.setState({ displayUserConfirmationModal: true });
  }

  _onNavigationModalResponse(leaveForm = false) {
    if (leaveForm) {
      if (this._popBlocked) {
        // Browser back was intercepted on stage > 0 — go back a stage, stay in the form
        this._popBlocked = false;
        this.props.updateFormData(this.props.type, { selectedConnection: null });
        this.props.formDidTransitionToPrev(this.props.type);
        this.setState({ displayUserConfirmationModal: false });
      } else {
        this.props.dismissForm(this.props.type);
        this.props.onDismiss();
      }
    } else {
      this._popBlocked = false;
      this.setState({
        displayUserConfirmationModal: false,
      });
    }
  }

  _handleBackButtonClicked() {
    const { type, formDidTransitionToPrev } = this.props;
    formDidTransitionToPrev(type);
  }

  // Jump directly to an earlier stage (e.g. clicking "Select" from "Configure").
  // Going back never validates, and the forms reducer just sets currentStage, so
  // this is equivalent to repeated back-transitions in one hop.
  _handleStageClicked(targetIndex) {
    const { type, setFormStageIndex } = this.props;
    setFormStageIndex(type, targetIndex);
  }

  _handleNextButtonClicked(nextButton = null) {
    const { data, currentStage } = this.props;
    const { stageComponents } = this.state;
    const currentStageComponent = stageComponents[currentStage];

    const { validationMethod, onClick } = nextButton || currentStageComponent.nextButtons[0];

    if (validationMethod) {
      this._setValidationStatus(true, null);
      validationMethod(data, this._handleValidationSuccess, this._handleValidationError);
    } else if (onClick) {
      onClick();
    } else {
      this._completeNextStageTransition();
    }
  }

  _handleValidationSuccess() {
    this._setValidationStatus(false, null);
    this._completeNextStageTransition();
  }

  _handleValidationError(error) {
    this._setValidationStatus(false, error);
  }

  _handleWindowResize() {
    this._measureContainerDim();
  }

  _completeNextStageTransition() {
    const { numStages, stageComponents } = this.state;
    const { type, currentStage, finishForm, formDidTransitionToNext, onSubmit } = this.props;

    if (currentStage === numStages - 1) {
      finishForm(type);
      onSubmit();
    } else {
      formDidTransitionToNext(type);

      const nextStageComponent = stageComponents[currentStage + 1];

      if (nextStageComponent && nextStageComponent.onEnter) {
        nextStageComponent.onEnter(currentStage + 1);
      }

      this.setState({
        currentStage: currentStage + 1,
        awaitingValidation: false,
        validationError: null,
      });
    }
  }

  _setValidationStatus(awaitingValidation, error) {
    const { type, setFormError, setFormAwaitingValidation, onValidationComplete } = this.props;

    setFormAwaitingValidation(type, awaitingValidation);
    setFormError(type, error);

    if (!awaitingValidation && onValidationComplete) {
      onValidationComplete();
    }
  }

  _measureContainerDim() {
    var containerHeight = this.formContentContainer.clientHeight;

    if (this.props.onFormContentResized) {
      //TODO:: Need to change so we do not harcode header height
      this.props.onFormContentResized(containerHeight);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentStage !== this.props.currentStage && this.props.didTransitionToStage) {
      this.props.didTransitionToStage(this.props.currentStage);
    }
  }

  componentDidMount() {
    if (this.props.onRef) {
      this.props.onRef(this);
    }

    this.props.startForm(this.props.type);

    window.addEventListener('resize', this._handleWindowResize);
    this._measureContainerDim();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleWindowResize);
    this.props.dismissForm(this.props.type);
  }

  render() {
    const {
      promptTitle,
      promptCancelBtnLabel,
      promptConfirmBtnLabel,
      promptMessage = exitWorkflowMessage,
      shouldRenderNextButton,
      isModal,
      onRunWithIngestion,
      cancelButtonText = 'Discard Changes',
    } = this.props;

    const currentStageIndex = this.props.currentStage || 0;
    const currentChild = this.state.children[currentStageIndex];
    const displayComponent = currentChild
      ? React.cloneElement(currentChild, {
          data: this.props.data,
          nextStage: this._handleNextButtonClicked,
          onRunWithIngestion,
        })
      : null;

    return (
      <div className={classNames('overlay-container', this.props.className)}>
        <div className="overlay-header">
          <FormHeader
            title={this.props.title}
            subtitle={this.props.subtitle}
            stagesLeftView={this.props.stagesLeftView}
            nextToLogoView={this.props.nextToLogoView}
            onCancelButtonClicked={this._handleCancelButtonClicked}
            onBackButtonClicked={this._handleBackButtonClicked}
            onStageClick={this._handleStageClicked}
            onNextButtonClicked={this._handleNextButtonClicked}
            submitButtonText={this.props.submitButtonText}
            cancelButtonText={cancelButtonText}
            stageComponents={this.state.stageComponents}
            activeStageIndex={currentStageIndex}
            awaitingValidation={
              this.props.awaitingValidation || this.props.blockContinueForCredentialProbe
            }
            alwaysShowCancel={this.props.alwaysShowCancel}
            showBackButton={this.props.showBackButton}
            shouldRenderNextButton={shouldRenderNextButton}
            hideContextualMenu={this.props.isModal}
          />
        </div>
        <div
          id="form:overlay-body"
          className="overlay-body"
          ref={(e) => {
            this.formContentContainer = e;
          }}
        >
          <FormErrorComponent
            type={this.props.type}
            error={this.props.error}
            dialogIsOpen={this.props.dialogIsOpen}
          />

          {!isModal && (
            <Prompt
              message={(location, action) => {
                if (
                  action === 'POP' &&
                  this.props.currentStage > 0 &&
                  !this.props.browserBackLeavesEditor
                ) {
                  // Browser back on a non-first stage: show the confirmation dialog in-tree,
                  // but on confirm go back a stage rather than navigating away entirely.
                  // Flex Flow sets browserBackLeavesEditor so POP uses the same path as Direct
                  // Flow (router confirmation, then history proceeds).
                  this._popBlocked = true;
                  this.setState({ displayUserConfirmationModal: true });
                  return SILENT_BLOCK_MESSAGE;
                }
                return promptMessage;
              }}
              when={!this.props.isComplete && this.state.displayUserConfirmationModal === false}
            />
          )}
          {this.state.displayUserConfirmationModal === true && !isModal && (
            <UserConfirmation
              title={promptTitle}
              message={promptMessage}
              okButtonLabel={promptConfirmBtnLabel}
              cancelButtonLabel={promptCancelBtnLabel}
              onCancel={() => this._onNavigationModalResponse(false)}
              onConfirm={() => this._onNavigationModalResponse(true)}
            />
          )}

          {this.state.displayUserConfirmationModal === true && isModal && (
            <UserConfirmation
              title="Exit Editor"
              message="Are you sure you want to exit this editor? All of your current changes will be discarded."
              okButtonLabel="Exit Editor"
              onCancel={() => this._onNavigationModalResponse(false)}
              onConfirm={() => this._onNavigationModalResponse(true)}
            />
          )}

          {displayComponent}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const formState = state.forms[ownProps.type];
  const sourceBeingEdited = ownProps.sourceBeingEdited;

  let connectionCategory;
  if (sourceBeingEdited?.vendor_id) {
    const vendors = state.entities.vendors;
    const vendorName = vendors[sourceBeingEdited.vendor_id]?.name;
    const paging = state.paging;
    connectionCategory = paging?.metaInfoById?.[vendorName]?.connectionCategory;
  }

  return {
    currentStage: formState && formState.currentStage,
    data: formState && formState.data,
    awaitingValidation: formState && formState.awaitingValidation,
    error: formState && formState.error,
    dialogIsOpen: formState && formState.dialogIsOpen,
    isComplete: formState && formState.isComplete,
    connectionCategory,
  };
};

export default connect(mapStateToProps, {
  startForm,
  formDidTransitionToNext,
  formDidTransitionToPrev,
  finishForm,
  dismissForm,
  setFormAwaitingValidation,
  setFormError,
  setFormStageIndex,
  updateFormData,
})(Form);
